import { useEffect } from "react"
import { action_getProductsByIdsOrHandles } from "src/components/(checkout)/cart/actions"
import { StorefrontBaseProduct, StorefrontListProduct } from "src/types/storefront"
import { createPersistentStore } from "src/utilities/localStorage/create"
import { create as createStore } from "zustand"

type WishlistState = Record<string, boolean>

const wishlistDefaultState: WishlistState = {}

const wishlistStore = createPersistentStore(() => wishlistDefaultState, {
  name: "wishlist",
})

type ProductsState = {
  initializing: boolean
  products: StorefrontListProduct[]
}

const productsDefaultState: ProductsState = {
  initializing: true,
  products: [],
}

const productsStore = createStore(() => productsDefaultState)

export function useWishlist() {
  const wishlist = wishlistStore()

  return wishlist
}

export function useWishlistCount() {
  const wishlist = wishlistStore()

  return Object.entries(wishlist).filter(([, value]) => value).length
}

export function useIsOnWishlist(product: StorefrontBaseProduct) {
  const wishlist = wishlistStore()
  const key = product.id

  return !!wishlist[key]
}

let updatePromise: Promise<void> | null = null

async function updateProducts() {
  const newWishlist = wishlistStore.getState()
  const { initializing, products: oldProducts } = productsStore.getState()

  const newIds = Object.entries(newWishlist).flatMap(([id, wishlisted]) => (wishlisted ? id : []))
  const oldIds = oldProducts.map((product) => product.id)

  if (newIds.sort().join() === oldIds.sort().join()) {
    if (initializing) {
      productsStore.setState({ initializing: false })
    }
    return
  }

  if (updatePromise) {
    return
  }

  const missingIds = newIds.filter((id) => !oldProducts.some((product) => product.id === id))

  updatePromise = (missingIds.length ? action_getProductsByIdsOrHandles(missingIds) : Promise.resolve([])).then(
    (missingProducts) => {
      const newProducts = newIds.flatMap((id) => {
        return (
          oldProducts.find((product) => product.id === id) || missingProducts.find((product) => product.id === id) || []
        )
      })

      for (const id of newIds) {
        if (!newProducts.some((product) => product.id === id)) {
          console.warn(`Product with id ${id} is missing from the wishlist`)
          removeFromWishlist(id)
        }
      }

      productsStore.setState({ products: newProducts, initializing: false })

      updatePromise = null
    },
  )
}

export function toggleWishlist(product: StorefrontBaseProduct) {
  const key = product.id
  const oldWishlist = wishlistStore.getState()
  const newWishlist = { ...oldWishlist, [key]: !oldWishlist[key] }

  wishlistStore.setState(newWishlist, true)

  updateProducts()
}

export function removeFromWishlist(key: string) {
  wishlistStore.setState((state) => ({ ...state, [key]: false }), true)
}

export function useWishlistProducts() {
  useEffect(() => {
    updateProducts()
  }, [])

  return productsStore()
}
